'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Settings, Heart } from 'lucide-react';
import CookieSettingsModal from './CookieSettingsModal';
import { useLanguage } from '@/context/LanguageContext';
import { getTranslations } from '@/lib/i18n';

export default function Footer() {
  const [cookieModalOpen, setCookieModalOpen] = useState(false);
  const { language } = useLanguage();
  const t = getTranslations(language);

  const legalLinks = [
    { label: t?.footer?.impressum, href: '/impressum' },
    { label: t?.footer?.privacy, href: '/impressum#datenschutz' },
    { label: t?.footer?.agb, href: '/impressum#agb' },
    { label: t?.footer?.revocation, href: '/impressum#widerruf' },
  ];

  return (
    <>
      <footer
        className="border-t mt-20"
        style={{ borderColor: 'var(--border)', background: 'var(--secondary)' }}
      >
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-3">
                <AppLogo size={32} />
                <span className="font-extrabold text-lg text-gradient-primary">PartyGames</span>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                {t?.footer?.description}
              </p>
            </div>

            {/* Spiele */}
            <div>
              <p className="section-header mb-3">{t?.footer?.gamesTitle}</p>
              <ul className="space-y-2">
                {['Impostor', 'Flaschendrehen', 'Busfahrer', 'Wahrheit oder Pflicht']?.map((game) => (
                  <li key={`footer-game-${game}`}>
                    <Link
                      href="/"
                      className="text-sm transition-colors hover:text-foreground"
                      style={{ color: 'var(--muted-foreground)' }}
                    >
                      {game}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Rechtliches */}
            <div>
              <p className="section-header mb-3">{t?.footer?.legalTitle}</p>
              <ul className="space-y-2">
                {legalLinks?.map((item) => (
                  <li key={`footer-legal-${item?.label}`}>
                    <Link
                      href={item?.href}
                      className="text-sm transition-colors hover:text-foreground"
                      style={{ color: 'var(--muted-foreground)' }}
                    >
                      {item?.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <button
                    onClick={() => setCookieModalOpen(true)}
                    className="text-sm transition-colors hover:text-foreground flex items-center gap-1"
                    style={{ color: 'var(--muted-foreground)' }}
                  >
                    <Settings size={12} />
                    {t?.footer?.cookieSettings}
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div
            className="border-t pt-6 flex flex-col sm:flex-row items-center justify-between gap-3"
            style={{ borderColor: 'var(--border)' }}
          >
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              {t?.footer?.copyright}
            </p>
            <p className="text-xs flex items-center gap-1" style={{ color: 'var(--muted-foreground)' }}>
              {t?.footer?.madeWith} <Heart size={12} className="text-neon-pink" /> {t?.footer?.inGermany}
            </p>
          </div>
        </div>
      </footer>
      <CookieSettingsModal open={cookieModalOpen} onClose={() => setCookieModalOpen(false)} />
    </>
  );
}